import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LegalCanal from './LegalCanal';
import { registroCompletar, registroIniciar, registroVerificar, mensajeError, errorCode } from '../../api/canalRegistro';

// Los tres codes que significan lo mismo de cara a la clienta: «este enlace ya
// no sirve». El servidor los distingue (caducado / inválido / ya usado); ella no
// necesita esa diferencia, necesita un enlace nuevo. UN SOLO SITIO donde está
// escrito qué cuenta como enlace muerto (Ann 2026-09-23: unificar, no aplanar).
const CODIGOS_ENLACE_MUERTO = ['TOKEN_CADUCADO', 'TOKEN_INVALIDO', 'TOKEN_YA_USADO'];

/* EMBUDO DE ALTA/SUSCRIPCIÓN del Canal (flujo A · hacia el pago). Ann 2026-07-11.
   Orden (el email se recoge en la HOME = paso 0):
     1) Hemos enviado un email a tu correo
     2) Elige tu contraseña
     3) Elige tu plan
     4) Rellena tus datos (nombre, apellidos, DNI, teléfono, dirección) — para factura
     5) Ir al pago (Stripe · Fase 2)
   Con validaciones de error (email no válido, DNI, campos obligatorios…).
   Estética = registro del canal (fondo oscuro + logo blanco Montserrat + tarjeta clara).
   Distinto del onboarding PREMIUM (flujo B, post-pago: normativa + tutorial). */

const TEAL = '#2C6675';
const TEAL2 = '#3C8696';
const ERR = '#d9534f';

const inputStyle = (err) => ({
  width: '100%', height: 42, borderRadius: 10, border: `1px solid ${err ? '#e3a6a3' : '#dbe6e7'}`,
  padding: '0 12px', fontSize: 14.5, outline: 'none', boxSizing: 'border-box', marginTop: 4,
  fontFamily: "'Montserrat', system-ui, sans-serif",
});
const pill = (activo) => ({
  height: 48, borderRadius: 12, border: 'none', cursor: activo ? 'pointer' : 'default', width: '100%',
  fontWeight: 800, fontSize: 15.5, fontFamily: "'Montserrat', system-ui, sans-serif",
  background: activo ? `linear-gradient(135deg,${TEAL2},${TEAL})` : '#cfe0e1', color: '#fff',
});
const ghost = {
  width: '100%', height: 40, marginTop: 12, borderRadius: 12, background: 'rgba(60,134,150,.10)',
  color: TEAL, border: '1px solid rgba(60,134,150,.35)', fontWeight: 600, fontSize: 14.5, cursor: 'pointer',
  fontFamily: "'Montserrat', system-ui, sans-serif",
};
const lbl = { fontWeight: 700, fontSize: 13, color: '#16323a', display: 'block' };
const errText = { color: ERR, fontSize: 12.5, margin: '4px 0 0' };
const _debiles = /^(?:1234\d*|12345678|123456789|0000+|1111+|password|passw0rd|contrasena|contraseña|qwerty|abcdef|admin\d*)$/i;

const emailValido = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// ═══ CONTRATO ═══
// QUÉ HACE: añade a la URL de pago el ID DE LA FICHA de quien está pagando.
// NO SE PUEDE CAMBIAR: no volver a mandar a la pasarela sin el id. Hasta el
//   23-09-2026 el salto iba pelado, así que el pago solo se podía emparejar por
//   EL CORREO QUE ELLA TECLEA ALLÍ — y si ponía otro, pagaba y se quedaba fuera.
//   El id NO lo teclea nadie: lo devuelve `/registro/completar` (`user_id`).
//   Tampoco quitar el `encodeURIComponent`: el id viaja en una URL.
// POR QUÉ EXISTE: 23-09-2026 · atar el pago a la ficha por identificador.
//   Lo lee el webhook en `client_reference_id` (stripe_webhook.py, PR #1856).
// ⚠️ Si la pasarela IGNORA el parámetro, no rompe nada: queda como hoy. Que lo
//   acepte de verdad está por comprobar contra la cuenta de Stripe.
// LO VIGILA: test_TESTAUTOPISTA_el_pago_lleva_el_id_de_la_clienta.py
export function conIdDeLaClienta(url, userId) {
  if (!url || !userId) return url;          // sin id, mejor el enlace de siempre
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}client_reference_id=${encodeURIComponent(userId)}`;
}

// Planes CLUB reales (Guardián/Ann 2026-07-11). Dos modalidades: CLUB 63€/mes · PREMIUM 87€/mes.
// Textos por i18n (bilingüe ES/CA · F4): función de t; 'CLUB' es marca y no se traduce.
const construirPlanes = (t) => [
  {
    id: 'club', nombre: 'CLUB', tag: t('funnel.planTag'), precio: '63', periodo: t('funnel.porMes'),
    grad: 'linear-gradient(135deg,#3C8696,#2C6675)',
    features: [
      { l: t('funnel.f1l'), v: t('funnel.f1v') },
      { l: t('funnel.f2l'), v: t('funnel.si') },
      { l: t('funnel.f3l'), v: t('funnel.si') },
      { l: t('funnel.f4l'), v: t('funnel.si') },
      { l: t('funnel.f5l'), v: t('funnel.si') },
      { l: t('funnel.f6l'), v: t('funnel.si') },
      { l: t('funnel.f7l'), v: t('funnel.si') },
      { l: t('funnel.f8l'), v: t('funnel.si') },
      { l: t('funnel.f9l'), v: t('funnel.si') },
      { l: t('funnel.f10l'), v: t('funnel.si') },
      { l: t('funnel.f11l'), v: t('funnel.f11v') },
      { l: t('funnel.f12l'), v: t('funnel.si') },
      { l: t('funnel.f13l'), v: '—' },
      { l: t('funnel.f14l'), v: '—' },
    ],
  },
  // 🔒 Fase 1 = SOLO CLUB (Ann/Guardián 2026-07-11). PREMIUM y VIP → Fase 2 (Stripe propio en el CRM).
];

const wrap = {
  position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto',
  background: "linear-gradient(rgba(8,20,16,.82),rgba(8,16,12,.92)), url('/fondo-canal.jpg') center/cover no-repeat, linear-gradient(160deg,#0c1f16,#06110d)",
  fontFamily: "'Montserrat', system-ui, sans-serif",
  display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 18px 40px',
};
const card = { background: '#fff', borderRadius: 22, boxShadow: '0 18px 50px rgba(0,0,0,.35)', width: '100%', maxWidth: 460, padding: '30px 28px 28px', boxSizing: 'border-box' };
const h1 = { margin: '0 0 8px', fontSize: 23, color: '#16323a', fontWeight: 800, textAlign: 'center' };
const sub = { margin: '0 0 22px', color: '#33474d', fontSize: 14.5, lineHeight: 1.5, textAlign: 'center' };
const iconBox = (col) => ({ width: 66, height: 66, borderRadius: '50%', background: `${col}1f`, display: 'grid', placeItems: 'center', margin: '2px auto 18px' });
const gap = { height: 6 };
const row = { display: 'flex', gap: 12 };

const TOTAL = 5; // pasos del embudo (tras la home)

function LogoBlanco() {
  const { t } = useTranslation('auth');
  return (
    <div style={{ display: 'inline-block', textAlign: 'center', color: '#fff', lineHeight: 1, fontSize: 'clamp(22px,3.6vw,38px)', marginBottom: 20 }}>
      <div style={{ display: 'inline-flex', alignItems: 'flex-start', whiteSpace: 'nowrap', fontWeight: 500, letterSpacing: '.02em' }}>
        CANAL&nbsp;PILAR&nbsp;DOMÍNGUEZ
        <span style={{ fontSize: '.48em', fontWeight: 500, marginLeft: '.08em', alignSelf: 'flex-start', lineHeight: 1 }}>®</span>
      </div>
      <div style={{ fontSize: '0.25em', letterSpacing: '.14em', fontWeight: 500, marginTop: '0.8em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{t('pantallas.tagline')}</div>
    </div>
  );
}

export default function AltaFunnelCanal({ email = 'tu@email.com', onFinish = () => {}, demo = false, maqueta = false, handoffUrl = '', onIniciarSesion = () => { window.location.href = '/entrar'; } }) {
  const { t } = useTranslation('auth');
  // Si llegamos con `token` en la URL, el email YA está verificado (venimos de
  // /verificar-email) → arrancamos en el paso 1 (crear contraseña), NO en el
  // paso 0 (la pantalla "te hemos enviado un correo"). Antes arrancaba siempre
  // en 0 y, tras confirmar, «continuar» devolvía a "te hemos enviado" → bucle.
  const _tokenInicial = (() => { try { return new URLSearchParams(window.location.search).get('token') || ''; } catch (e) { return ''; } })();
  // Paso inicial ROBUSTO (Ann 18-jul: "una vez verificada NO vuelve atrás"):
  //  - guardamos el paso alcanzado en sessionStorage por token.
  //  - si hay token (email verificado) arrancamos como mínimo en el paso 1.
  //  Así, aunque el componente se re-monte o se pierda el token de la URL, NO
  //  vuelve a "te hemos enviado". El paso 0 solo se ve si NO hay token.
  const _pasoGuardado = (() => { try { const v = sessionStorage.getItem('canal_alta_step_' + (_tokenInicial || 'sin')); return v ? Math.max(1, parseInt(v, 10) || 1) : 0; } catch (e) { return 0; } })();
  const [step, setStep] = useState(_tokenInicial ? Math.max(1, _pasoGuardado) : 0);
  const [pw2, setPw2] = useState('');
  const [reenviado, setReenviado] = useState(false);
  const [emailLocal, setEmailLocal] = useState(email);
  const [editandoEmail, setEditandoEmail] = useState(false);
  const [emailBorrador, setEmailBorrador] = useState(email);
  const [pw, setPw] = useState('');
  const [quieroPromo, setQuieroPromo] = useState(false);
  const [aceptaLegal, setAceptaLegal] = useState(false);
  const [hp, setHp] = useState(''); // honeypot anti-bot (debe quedar vacío)
  const [startedAt] = useState(() => Date.now()); // para el check <2s en servidor
  const [pagando, setPagando] = useState(false);
  const [errPago, setErrPago] = useState('');
  const [enlaceManual, setEnlaceManual] = useState('');  // salida si no redirige
  const [enlaceMuerto, setEnlaceMuerto] = useState(false); // el enlace del correo ya no sirve
  const token = (() => { try { return new URLSearchParams(window.location.search).get('token') || ''; } catch (e) { return ''; } })();
  // ⚠️ Backend debe guardar la aceptación legal con fecha/hora + versión de documento
  // (p.ej. 'condiciones-canal v1 · privacidad-canal v1') como prueba de consentimiento RGPD.
  const [plan, setPlan] = useState('club'); // Fase 1: único plan, autoseleccionado.
  const [d, setD] = useState({ nombre: '', primer_apellido: '', segundo_apellido: '', dni: '', telefono: '', direccion: '', numero_piso: '', cp: '', ciudad: '', provincia: '' });
  const [tocado, setTocado] = useState({});
  const setF = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const marca = (k) => () => setTocado({ ...tocado, [k]: true });

  const PLANES = construirPlanes(t);
  const planSel = PLANES.find(p => p.id === plan);

  // Persistir el paso alcanzado → una vez verificada NO vuelve atrás (Ann 18-jul).
  useEffect(() => {
    try { if (token) sessionStorage.setItem('canal_alta_step_' + token, String(step)); } catch (e) {}
  }, [step, token]);

  // ═══ CONTRATO ═══
  // QUÉ HACE: al llegar con un token, PREGUNTA al servidor si sigue vivo. Si está
  //   caducado o ya usado, lo dice en el acto y ofrece pedir un enlace nuevo.
  // NO SE PUEDE CAMBIAR: no volver a tragarse el error de `verificar` con un
  //   `.catch(() => {})`. Hasta el 23-09-2026 se tragaba, y además solo se
  //   preguntaba cuando faltaba el email. Resultado: con el enlace MUERTO la
  //   pantalla se comportaba como si todo fuera bien, la clienta escribía
  //   contraseña, nombre y teléfono, y solo al final —tras rellenarlo todo—
  //   reventaba con TOKEN_INVALIDO. Sin salida, sin poder pedir otro enlace.
  // POR QUÉ EXISTE: recorrido REAL del alta con Ann, 23-09-2026. El enlace dura
  //   2 h y sirve UNA sola vez: quien va a buscar la tarjeta y vuelve luego se
  //   encuentra el enlace muerto. Es el caso de Anna y Marta.
  // LO VIGILA: test_TESTAUTOPISTA_el_alta_dice_la_verdad.py
  useEffect(() => {
    let vivo = true;
    if (token) {
      registroVerificar(token).then(r => {
        if (!vivo) return;
        if (r?.data?.email) { setEmailLocal(r.data.email); setEmailBorrador(r.data.email); }
      }).catch(err => {
        if (!vivo) return;
        if (CODIGOS_ENLACE_MUERTO.includes(errorCode(err))) setEnlaceMuerto(true);
      });
    }
    return () => { vivo = false; };
  }, [token]);

  // ═══ CONTRATO ═══
  // QUÉ HACE: reenvía el correo de verificación y dice la VERDAD de si salió.
  // NO SE PUEDE CAMBIAR: no volver a marcar «Reenviado ✓» en el `finally`. Hasta
  //   el 23-09-2026 se marcaba pasara lo que pasara, así que con un 429 (límite
  //   de 3 envíos/15 min) la persona se quedaba esperando un correo que NO iba a
  //   llegar. Tampoco distinguir aquí si el email existe: el 200 es genérico a
  //   propósito (anti-enumeración) y así debe seguir.
  // POR QUÉ EXISTE: recorrido de pantalla del alta, 23-09-2026 — la clienta que
  //   se atasca necesita saber qué ha pasado de verdad, no un ✓ de adorno.
  // LO VIGILA: test_TESTAUTOPISTA_el_alta_dice_la_verdad.py
  const [reenviando, setReenviando] = useState(false);
  const [errReenvio, setErrReenvio] = useState('');
  const reenviarEmail = async () => {
    if (reenviando) return;
    setReenviando(true); setErrReenvio(''); setReenviado(false);
    try {
      await registroIniciar(emailLocal);
      setReenviado(true);              // el servidor lo aceptó (200 genérico)
    } catch (e) {
      // 429 = ha pedido demasiados. Cualquier otro = no salió. En los dos casos
      // se le dice, y se le ofrece salida (el contacto de ayuda está siempre abajo).
      setErrReenvio(e?.response?.status === 429
        ? t('funnel.reenvioDemasiados')
        : t('funnel.reenvioFallo'));
    } finally { setReenviando(false); }
  };
  const guardarEmail = async () => {
    const e = emailBorrador.trim();
    if (!emailValido(e)) return;
    setEmailLocal(e); setEditandoEmail(false);
    try { await registroIniciar(e); } catch (_) {}
    setReenviado(true);
  };

  // Contraseña
  const reglasPw = [
    { ok: pw.length >= 8, txt: t('comun.reglaMin8') },
    { ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw), txt: t('comun.reglaMayusMinus') },
    { ok: /\d/.test(pw), txt: t('comun.reglaNumero') },
    { ok: pw.length > 0 && !_debiles.test(pw), txt: t('comun.reglaTipica') },
  ];
  const pwFuerte = reglasPw.every(r => r.ok);
  const pwsCoinciden = pw.length > 0 && pw === pw2;   // Ann 18-jul: contraseña dos veces

  // Validación datos (paso 4): solo Nombre + Primer apellido obligatorios antes del pago.
  // El resto (segundo apellido, DNI, teléfono, dirección…) se pide tras el pago en "Completa tu perfil".
  const req = (v) => !!v.trim();
  const telOk = (v) => ((v || '').replace(/\D/g, '').length >= 9);
  const datosOk = req(d.nombre) && req(d.primer_apellido) && telOk(d.telefono);

  // ── NAVEGACIÓN DE PRUEBA (solo la ve Ann): rellena datos falsos y avanza ──
  const demoMode = demo || (typeof window !== 'undefined' && (window.location.pathname.includes('-preview') || new URLSearchParams(window.location.search).get('demo') === '1'));
  const rellenarPaso = (s) => {
    if (s === 1) { setPw('PruebaCanal24'); setPw2('PruebaCanal24'); setAceptaLegal(true); }
    else if (s === 2) { if (!plan) setPlan('club'); }
    else if (s === 3) setD({ nombre: 'Ana', primer_apellido: 'García', segundo_apellido: 'López', dni: '12345678Z', telefono: '600123123', direccion: 'Calle Ejemplo 12', numero_piso: '3º 2ª', cp: '08006', ciudad: 'Barcelona', provincia: 'Barcelona' });
  };
  const demoSiguiente = () => { rellenarPaso(step); setReenviado(false); if (step < TOTAL - 1) setStep(step + 1); else onFinish(); };

  // POST /canal/registro/completar → 200 {ok,next:"pago"} → redirect al checkout UMP.
  // PAGO ACTIVADO (orden Ann 2026-07-18): tras completar el alta (cuenta creada con
  // pago_pendiente=True → bloqueada), se REDIRIGE al checkout de Club de la web
  // (UMP/Stripe, ?lid=4). Al pagar, UMP dispara el webhook "activated" → el CRM pone
  // pago_pendiente=False y se abre el acceso. ⚠️ El comprador debe pagar con el MISMO
  // email del alta para que el webhook lo empareje.
  // [Ann 2026-10-23] WordPress cerrado: el pago lo crea el CRM en Stripe y viene en `pago_url`.
  const destinoRef = useRef('');
  // ═══ CONTRATO ═══
  // QUÉ HACE: crea la cuenta (bloqueada, sin pagar) y lleva al checkout.
  // NO SE PUEDE CAMBIAR: no dejar «Procesando…» sin salida. Hasta el 23-09-2026
  //   `setPagando(false)` solo existía en la rama de error, así que si la
  //   redirección no ocurría (JS bloqueado, fallo de red) la persona se quedaba
  //   mirando «Procesando…» para siempre, sin mensaje y sin enlace. Ahora hay un
  //   plazo tras el cual SIEMPRE se le ofrece el enlace a mano.
  // POR QUÉ EXISTE: recorrido de pantalla del alta, 23-09-2026.
  // LO VIGILA: test_TESTAUTOPISTA_el_alta_dice_la_verdad.py
  const SEGUNDOS_HASTA_OFRECER_ENLACE = 6;
  const irAlPago = async () => {
    if (maqueta) { if (handoffUrl) window.location.href = handoffUrl; else onFinish(); return; }
    setPagando(true); setErrPago(''); setEnlaceManual('');
    // Red de seguridad: si en unos segundos seguimos aquí, es que la redirección
    // no ha ocurrido. No se deja a nadie mirando un «Procesando…» mudo.
    setTimeout(() => { if (destinoRef.current) setEnlaceManual(destinoRef.current); },
               SEGUNDOS_HASTA_OFRECER_ENLACE * 1000);
    try {
      const r = await registroCompletar({
        token, password: pw,
        acepta_privacidad: aceptaLegal, acepta_comunicaciones: quieroPromo,
        nombre: d.nombre, primer_apellido: d.primer_apellido,
        // El segundo apellido SE ESCRIBÍA en el formulario pero NO se enviaba:
        // se perdía en cada alta (Ann 2026-07-20: «ha de ser opcional pero ha de recogerse»).
        segundo_apellido: d.segundo_apellido,
        telefono: d.telefono,
        hp, started_at: startedAt,
      });
      if (r.data?.ok !== false) {
        // Cuenta creada (bloqueada) → al checkout de Club a pagar, CON SU ID.
        const destino = r.data?.pago_url || (handoffUrl ? conIdDeLaClienta(handoffUrl, r.data?.user_id) : '');
        if (!destino) { setErrPago(t('funnel.errorRegistro')); setPagando(false); return; }
        destinoRef.current = destino;
        // La salida de emergencia («si no se abre, pulsa aquí») tiene que llevar
        // el id TAMBIÉN: si no, quien la use pagaría sin atar el pago a su ficha
        // — justo el fallo que esto viene a cerrar.
        setEnlaceManual(destino);
        window.location.href = destino;
      }
      else { setErrPago(t('funnel.errorRegistro')); setPagando(false); }
    } catch (err) {
      // Si lo que ha muerto es el ENLACE, no se le enseña un error rojo y se la
      // deja ahí: se le ofrece pedir uno nuevo, que es lo único que la desatasca.
      if (CODIGOS_ENLACE_MUERTO.includes(errorCode(err))) { setEnlaceMuerto(true); setPagando(false); return; }
      setErrPago(mensajeError(err)); setPagando(false);
    }
  };

  // ═══ CONTRATO ═══
  // QUÉ HACE: pide un enlace NUEVO para el correo que escriba la clienta.
  // NO SE PUEDE CAMBIAR: no dar «enviado ✓» sin que el servidor lo acepte (misma
  //   norma que `reenviarEmail`), y no revelar si el email existe: `iniciar`
  //   devuelve 200 genérico a propósito (anti-enumeración) y así debe seguir.
  // POR QUÉ EXISTE: el enlace dura 2 h y vale UNA vez. Sin esto, quien lo deja a
  //   medias se queda fuera para siempre y solo la rescata un administrador a
  //   mano — algo que no tiene botón en ninguna pantalla y no hace nadie.
  // LO VIGILA: test_TESTAUTOPISTA_el_alta_dice_la_verdad.py
  const [enlaceNuevoPedido, setEnlaceNuevoPedido] = useState(false);
  const pedirEnlaceNuevo = async () => {
    if (reenviando) return;
    const destino = (emailBorrador || emailLocal || '').trim();
    if (!emailValido(destino)) return;
    setReenviando(true); setErrReenvio(''); setEnlaceNuevoPedido(false);
    try {
      await registroIniciar(destino);
      setEmailLocal(destino);
      setEnlaceNuevoPedido(true);
    } catch (e) {
      setErrReenvio(e?.response?.status === 429
        ? t('funnel.reenvioDemasiados')
        : t('funnel.reenvioFallo'));
    } finally { setReenviando(false); }
  };
  const demoAtras = () => { setReenviado(false); if (step > 0) setStep(step - 1); };

  // ── ENLACE MUERTO · pantalla propia, con salida ────────────────────────────
  // Se enseña ANTES que cualquier paso: no tiene sentido pedirle contraseña a
  // quien viene con un enlace que ya no vale. Aquí SIEMPRE hay una salida que
  // ella puede usar sola, sin llamar a nadie.
  if (enlaceMuerto) {
    const destino = (emailBorrador || emailLocal || '').trim();
    return (
      <div style={wrap}>
        <LogoBlanco />
        <div style={card}>
          <div style={iconBox(TEAL)}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
          </div>
          <h1 style={h1}>{t('funnel.enlaceCaducadoTitulo')}</h1>

          {enlaceNuevoPedido ? (
            <>
              <p style={sub}>{t('funnel.enlaceNuevoEnviado', { email: emailLocal })}</p>
              <p style={{ fontSize: 13, color: '#8a9ca0', textAlign: 'center', margin: '0 0 16px' }}>{t('funnel.noLoEncuentras')}</p>
            </>
          ) : (
            <>
              <p style={sub}>{t('funnel.enlaceCaducadoSub')}</p>
              <input
                type="email"
                value={emailBorrador}
                onChange={e => setEmailBorrador(e.target.value.trim())}
                placeholder={t('comun.emailPlaceholder')}
                style={inputStyle(destino.length > 0 && !emailValido(destino))}
              />
              {destino.length > 0 && !emailValido(destino) && <p style={errText}>{t('funnel.correoInvalido')}</p>}
              <div style={{ marginTop: 14 }}>
                <button style={pill(emailValido(destino) && !reenviando)} disabled={!emailValido(destino) || reenviando} onClick={pedirEnlaceNuevo}>
                  {reenviando ? t('funnel.enviando') : t('funnel.enlaceCaducadoBoton')}
                </button>
              </div>
              {errReenvio && <p style={{ ...errText, textAlign: 'center', margin: '10px 0 0' }}>{errReenvio}</p>}
            </>
          )}

          <p style={{ fontSize: 12.5, color: '#8a9ca0', textAlign: 'center', margin: '18px 0 0', lineHeight: 1.6 }}>
            {t('funnel.ayudaContacto')} <b>mpdonline@metodopilardominguez.com</b>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      {/* Iniciar sesión visible en todo momento (arriba a la derecha) */}
      <button onClick={onIniciarSesion} style={{ position: 'fixed', top: 20, right: 'clamp(16px,4vw,40px)', zIndex: 10002, height: 38, padding: '0 18px', borderRadius: 9, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#3C8696,#2C6675)', color: '#fff', fontSize: 14.5, fontWeight: 800, fontFamily: "'Montserrat', system-ui, sans-serif" }}>{t('comun.iniciarSesion')}</button>
      <LogoBlanco />

      {/* Progreso · Paso X de 5 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {Array.from({ length: TOTAL }).map((_, i) => (
          <span key={i} style={{ width: i === step ? 26 : 8, height: 8, borderRadius: 999, background: i <= step ? '#7fd8b8' : 'rgba(255,255,255,.28)', transition: '.25s' }} />
        ))}
      </div>
      <p style={{ color: 'rgba(255,255,255,.75)', fontSize: 12.5, margin: '0 0 16px' }}>{t('funnel.pasoDe', { actual: step + 1, total: TOTAL })}</p>

      <div style={step === 2 ? { ...card, maxWidth: 440, padding: '30px 26px 26px' } : step === 3 ? { ...card, maxWidth: 520 } : card}>
        {/* Anti-bot: honeypot oculto (los bots lo rellenan) + started_at (check <2s en servidor). */}
        <input type="text" name="empresa_web" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} data-started={startedAt} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

        {/* ── 1 · HEMOS ENVIADO UN EMAIL ── */}
        {step === 0 && (
          <div>
            <div style={iconBox(TEAL)}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m3 7 9 6 9-6" /></svg>
            </div>
            <h1 style={h1}>{t('funnel.emailEnviado')}</h1>

            {editandoEmail ? (
              <>
                <p style={sub}>{t('funnel.corrigeSub')}</p>
                <input type="email" value={emailBorrador} onChange={e => setEmailBorrador(e.target.value.trim())} placeholder={t('comun.emailPlaceholder')} style={inputStyle(emailBorrador.length > 0 && !emailValido(emailBorrador))} />
                {emailBorrador.length > 0 && !emailValido(emailBorrador) && <p style={errText}>{t('funnel.correoInvalido')}</p>}
                <div style={{ marginTop: 14 }}>
                  <button style={pill(emailValido(emailBorrador))} disabled={!emailValido(emailBorrador)} onClick={guardarEmail}>{t('funnel.guardarReenviar')}</button>
                </div>
                <button onClick={() => { setEditandoEmail(false); setEmailBorrador(emailLocal); }} style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: '#5a7176', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>{t('comun.cancelar')}</button>
              </>
            ) : (
              <>
                <p style={sub}>{t('funnel.enviado1')}<b>{emailLocal}</b>{t('funnel.enviadoDesde')}<b>mpdonline@metodopilardominguez.com</b>{t('funnel.enviado2')}</p>
                <p style={{ fontSize: 13, color: '#8a9ca0', textAlign: 'center', margin: '0 0 16px' }}>{t('funnel.noLoEncuentras')}</p>
                <button onClick={reenviarEmail} disabled={reenviando} style={{ ...ghost, marginTop: 0, opacity: reenviando ? 0.6 : 1, cursor: reenviando ? 'default' : 'pointer' }}>
                  {reenviando ? 'Enviando…' : (reenviado ? 'Reenviado ✓' : t('funnel.reenviar'))}
                </button>
                {/* Si NO salió, se dice. Antes se ponía «Reenviado ✓» igual. */}
                {errReenvio && (
                  <p style={{ ...errText, textAlign: 'center', margin: '10px 0 0' }}>{errReenvio}</p>
                )}
                <button onClick={() => { setEmailBorrador(emailLocal); setEditandoEmail(true); }} style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: TEAL, fontSize: 14, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>{t('funnel.equivocadoCorreo')}</button>
              </>
            )}
          </div>
        )}

        {/* ── 2 · ELIGE TU CONTRASEÑA ── */}
        {step === 1 && (
          <div>
            <h1 style={{ ...h1, marginBottom: 6 }}>{t('funnel.terminaConfigurar')}</h1>
            <p style={{ margin: '0 0 22px', color: '#5a7176', fontSize: 14, textAlign: 'center' }}>{t('funnel.eligePasswordPara')}<b style={{ color: '#16323a' }}>{emailLocal}</b>.</p>
            <label style={lbl}>{t('funnel.tuEmailLabel')}</label>
            <input value={emailLocal} disabled style={{ ...inputStyle(false), background: '#f2f7f7', color: '#3a4e53', margin: '6px 0 16px' }} />
            <label style={lbl}>{t('comun.password')}</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder={t('comun.min8Placeholder')} style={{ ...inputStyle(false), margin: '6px 0 12px' }} />
            <label style={lbl}>Repite la contraseña</label>
            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Repite la contraseña" style={{ ...inputStyle(pw2.length > 0 && !pwsCoinciden), margin: '6px 0 4px' }} />
            {pw2.length > 0 && !pwsCoinciden && <p style={{ margin: '0 0 8px', color: ERR, fontSize: 12.5 }}>Las contraseñas no coinciden</p>}
            {pw.length > 0 && (
              <div style={{ margin: '2px 0 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {reglasPw.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: r.ok ? '#2f7d5a' : '#8a7a5a' }}>
                    <span style={{ width: 15, height: 15, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', background: r.ok ? '#2f7d5a' : '#e2d9c6', color: '#fff', fontSize: 10 }}>{r.ok ? '✓' : ''}</span>{r.txt}
                  </div>
                ))}
              </div>
            )}
            {/* Consentimiento LEGAL (obligatorio) — separado del de marketing. Al aceptar se guarda
                fecha/hora + versión (DOC_VERSION) junto al registro como prueba de consentimiento (backend).
                ⚠️ NINGÚN ENLACE DENTRO DE ESTA ETIQUETA. Hasta el 24-09-2026 las dos
                palabras legales eran enlaces con `target="_blank"` metidos aquí: abrían
                otra pestaña, el alta se perdía entera (su estado vive solo en memoria)
                y no había forma de volver. Le pasaba justo a quien se leía la política
                antes de aceptar. El texto va AHORA debajo, en esta misma pantalla. */}
            <label style={{ display: 'flex', gap: 11, alignItems: 'flex-start', margin: '12px 0 10px', cursor: 'pointer', fontSize: 13.5, color: '#16323a', lineHeight: 1.4 }}>
              <input type="checkbox" checked={aceptaLegal} onChange={e => setAceptaLegal(e.target.checked)} style={{ marginTop: 2, width: 18, height: 18, accentColor: TEAL, flexShrink: 0 }} />
              <span>{t('funnel.legal1')}<b>{t('funnel.legalCondiciones')}</b>{t('funnel.legal2')}<b>{t('funnel.legalPrivacidad')}</b>{t('funnel.legal3')}</span>
            </label>
            <label style={{ display: 'flex', gap: 11, alignItems: 'flex-start', margin: '0 0 18px', cursor: 'pointer', fontSize: 13.5, color: '#16323a', lineHeight: 1.4 }}>
              <input type="checkbox" checked={quieroPromo} onChange={e => setQuieroPromo(e.target.checked)} style={{ marginTop: 2, width: 18, height: 18, accentColor: TEAL, flexShrink: 0 }} />
              <span>{t('funnel.promo')} <span style={{ color: '#8a9ca0' }}>{t('comun.opcional')}</span></span>
            </label>
            <button style={pill(pwFuerte && pwsCoinciden && aceptaLegal)} disabled={!pwFuerte || !pwsCoinciden || !aceptaLegal} onClick={() => setStep(2)}>{t('comun.continuar')}</button>
            {/* LOS TEXTOS LEGALES, AQUÍ ABAJO Y EN LETRA PEQUEÑA (Ann 24-09-2026).
                La obligación es que estén DISPONIBLES, no que se lean: «si le doy
                a aceptar sin leer nada, es mi problema». Por eso NO bloquean el
                botón, no hay que desplazarse hasta el final ni hay temporizador:
                la casilla de arriba sigue siendo la única puerta.
                Salen del DOCUMENTO PUBLICADO (mismo componente que /privacidad y
                /condiciones), nunca copiados aquí: si se copiaran, el día que Ann
                cambie la política esta pantalla seguiría enseñando la vieja. */}
            <LegalCanal tipo="privacidad" inline />
            <LegalCanal tipo="condiciones" inline />
          </div>
        )}

        {/* ── 3 · ELIGE TU PLAN ── */}
        {step === 2 && (
          <div>
            <h1 style={{ ...h1, fontSize: 24 }}>{t('funnel.tuSuscripcion')}</h1>
            <p style={sub}>{t('funnel.suscripcionSub')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
              {PLANES.map(p => {
                const s = plan === p.id;
                return (
                  <div key={p.id} onClick={() => setPlan(p.id)} style={{ border: `2px solid ${s ? TEAL : '#e6eeed'}`, borderRadius: 16, overflow: 'hidden', cursor: 'pointer', background: '#fff', boxShadow: s ? '0 12px 30px rgba(44,107,118,.18)' : '0 6px 18px rgba(0,0,0,.05)', position: 'relative' }}>
                    {p.destacado && <div style={{ position: 'absolute', top: 12, right: 12, background: '#fff', color: TEAL, fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 999 }}>{t('funnel.recomendado')}</div>}
                    <div style={{ background: p.grad, color: '#fff', padding: '18px 18px 16px', position: 'relative' }}>
                      <div style={{ fontSize: 19, fontWeight: 800 }}>{p.nombre}</div>
                      <div style={{ fontSize: 13, opacity: .92, marginTop: 2 }}>{p.tag}</div>
                      {s && <div style={{ position: 'absolute', bottom: 12, right: 14 }}><svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><circle cx="12" cy="12" r="12" fill="rgba(255,255,255,.25)" /><path d="M17 8.5 10.5 15 7 11.5" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>}
                    </div>
                    <div style={{ padding: '16px 18px 18px' }}>
                      <div style={{ fontSize: 12.5, color: '#4a5f63', fontWeight: 600 }}>{t('funnel.precioMensual')}</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#16323a', marginBottom: 12 }}>{p.precio}<span style={{ fontSize: 13, fontWeight: 600, color: '#5a7176' }}> €{p.periodo}</span></div>
                      {p.features.map((f, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', padding: '5px 0', borderTop: '1px solid #eef4f3' }}>
                          <span style={{ fontSize: 12, color: '#4a5f63', lineHeight: 1.3 }}>{f.l}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, textAlign: 'right', flexShrink: 0, maxWidth: '45%', color: f.v === '—' ? '#9aacab' : (f.v === t('funnel.si') ? '#2f7d5a' : '#16323a') }}>{f.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ maxWidth: 460, margin: '20px auto 0' }}>
              <button style={pill(!!plan)} disabled={!plan} onClick={() => setStep(3)}>{t('comun.continuar')}</button>
            </div>
          </div>
        )}

        {/* ── 4 · RELLENA TUS DATOS ── */}
        {step === 3 && (
          <div>
            <h1 style={h1}>{t('funnel.rellenaDatos')}</h1>
            <p style={sub}>{t('funnel.rellenaSub')}</p>

            <div style={row}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>{t('comun.nombre')} *</label>
                <input value={d.nombre} onChange={setF('nombre')} onBlur={marca('nombre')} placeholder={t('comun.nombre')} style={inputStyle(tocado.nombre && !req(d.nombre))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>{t('comun.primerApellido')} *</label>
                <input value={d.primer_apellido} onChange={setF('primer_apellido')} onBlur={marca('primer_apellido')} placeholder={t('comun.primerApellido')} style={inputStyle(tocado.primer_apellido && !req(d.primer_apellido))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>{t('comun.segundoApellido')}</label>
                <input value={d.segundo_apellido} onChange={setF('segundo_apellido')} placeholder={t('comun.segundoApellido')} style={inputStyle(false)} />
              </div>
            </div>
            <div style={row}>
              <div style={{ flex: 1 }}>
                {/* TELÉFONO obligatorio ANTES del pago (Ann 2026-07-15): con el pago
                    temporalmente inactivo, es el dato con el que el equipo la llama. */}
                <label style={lbl}>{t('comun.telefono')} *</label>
                <input value={d.telefono} onChange={setF('telefono')} onBlur={marca('telefono')} placeholder={t('comun.telefonoMovil')} type="tel" inputMode="tel"
                  style={inputStyle(tocado.telefono && !telOk(d.telefono))} />
              </div>
            </div>
            {(tocado.nombre && !req(d.nombre)) || (tocado.primer_apellido && !req(d.primer_apellido)) || (tocado.telefono && !telOk(d.telefono)) ? <p style={errText}>{t('funnel.obligatoriosTel')}</p> : <div style={gap} />}

            <button style={pill(datosOk)} disabled={!datosOk} onClick={() => setStep(4)}>{t('comun.continuar')}</button>
          </div>
        )}

        {/* ── 5 · IR AL PAGO ── */}
        {step === 4 && (
          <div>
            <h1 style={h1}>{t('funnel.irPago')}</h1>
            <p style={sub}>{t('funnel.irPagoSub')}</p>
            {planSel && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f5faf9', border: '1px solid #e2eeed', borderRadius: 12, padding: '13px 15px', marginBottom: 16 }}>
                <div><div style={{ fontSize: 12, color: '#7d9195' }}>{t('funnel.tuPlan')}</div><div style={{ fontWeight: 800, color: '#16323a' }}>{planSel.nombre}</div></div>
                <div style={{ fontWeight: 800, color: TEAL }}>{planSel.precio}<span style={{ fontSize: 12, fontWeight: 600, color: '#7d9195' }}> €{planSel.periodo}</span></div>
              </div>
            )}
            <div style={{ border: '1px dashed #c3d5d5', borderRadius: 14, padding: '20px 16px', textAlign: 'center', background: '#f5faf9', marginBottom: 16 }}>
              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
              </div>
              <div style={{ fontWeight: 800, color: '#16323a', marginBottom: 6 }}>{t('funnel.pagoSeguroTarjeta')}</div>
              <p style={{ fontSize: 13, color: '#3a4e53', margin: 0, lineHeight: 1.5 }}>{t('funnel.stripe1')}<b>{t('funnel.stripeStrong')}</b>{t('funnel.stripe2')}</p>
            </div>
            {/* 🔴 EL AVISO MÁS IMPORTANTE DE ESTA PANTALLA (23-09-2026).
                Al salto a la pasarela NO se le puede pasar el correo: la persona
                lo vuelve a escribir allí, y si pone OTRO, el pago no se empareja
                con su cuenta y se queda PAGANDO SIN ACCESO. Esto estaba avisado
                solo en un COMENTARIO del código; ahora se le dice a ella. */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fff8e6', border: '1px solid #f0d89a', borderRadius: 12, padding: '13px 15px', marginBottom: 14 }}>
              <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1.2 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: '#5a4a1e' }}>
                {t('funnel.mismoCorreoAviso')}<br />
                <b style={{ color: '#16323a', fontSize: 15, wordBreak: 'break-all' }}>{emailLocal}</b>
              </p>
            </div>
            <button style={pill(!pagando)} disabled={pagando} onClick={irAlPago}>{pagando ? t('funnel.procesando') : t('funnel.irPagoSeguro')}</button>
            {/* Si la redirección no ocurre, SIEMPRE hay una salida a mano. */}
            {enlaceManual && (
              <p style={{ fontSize: 13.5, textAlign: 'center', margin: '12px 0 0', color: '#3a4e53' }}>
                {t('funnel.noSeAbre')}{' '}
                <a href={enlaceManual} style={{ color: '#077069', fontWeight: 800 }}>{t('funnel.pulsaAqui')}</a>
              </p>
            )}
            {errPago && (
              <p style={{ ...errText, textAlign: 'center', margin: '10px 0 0' }}>
                {errPago}{' '}
                <a href="https://wa.me/34623036731?text=Hola,%20tengo%20problemas%20para%20completar%20mi%20alta%20en%20el%20Canal"
                  target="_blank" rel="noopener noreferrer" style={{ color: '#077069', fontWeight: 800 }}>
                  {t('funnel.ayudaAltaCorto')}
                </a>
              </p>
            )}
            <p style={{ fontSize: 13, color: '#3a4e53', textAlign: 'center', margin: '12px 0 0', fontWeight: 600 }}>{t('funnel.sinPermanencia')}</p>
            <p style={{ fontSize: 11.5, color: '#8a9ca0', textAlign: 'center', marginTop: 8 }}>{t('funnel.trasPago')}</p>
          </div>
        )}
      </div>

      {/* ── SALVAVIDAS (Ann 2026-08-05): que NADIE se quede atascada en el alta ── */}
      <a href="https://wa.me/34623036731?text=Hola,%20tengo%20problemas%20para%20completar%20mi%20alta%20en%20el%20Canal"
        target="_blank" rel="noopener noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 16, padding: '10px 18px', borderRadius: 999, background: 'rgba(7,26,26,.55)', border: '1px solid rgba(31,173,144,.45)', color: '#7fd8b8', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2a9.9 9.9 0 0 0-8.4 15.14L2 22l4.98-1.3A9.9 9.9 0 1 0 12.04 2zm0 1.8a8.1 8.1 0 0 1 6.86 12.4 8.1 8.1 0 0 1-10.2 3.03l-.36-.2-2.95.77.79-2.87-.23-.37A8.1 8.1 0 0 1 12.04 3.8z"/></svg>
        {t('funnel.ayudaAlta')}
      </a>
      <a href="mailto:mpdonline@metodopilardominguez.com?subject=Problema%20con%20mi%20alta%20en%20el%20Canal"
        style={{ marginTop: 8, color: 'rgba(255,255,255,.75)', fontSize: 13, textDecoration: 'underline' }}>
        {t('funnel.ayudaAltaEmail')}
      </a>

      {/* ── BARRA DE NAVEGACIÓN DE PRUEBA (solo visible en modo demo, para Ann) ── */}
      {demoMode && (
        <div style={{ position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 10001, display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(16,26,22,.92)', border: '1px solid rgba(127,216,184,.4)', borderRadius: 999, padding: '8px 10px 8px 16px', boxShadow: '0 10px 30px rgba(0,0,0,.4)' }}>
          <span style={{ color: '#7fd8b8', fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>{t('funnel.modoPrueba', { n: step + 1, total: TOTAL })}</span>
          <button onClick={demoAtras} disabled={step === 0} style={{ height: 34, padding: '0 14px', borderRadius: 999, border: '1px solid rgba(255,255,255,.3)', background: 'transparent', color: step === 0 ? 'rgba(255,255,255,.35)' : '#fff', fontWeight: 700, fontSize: 13.5, cursor: step === 0 ? 'default' : 'pointer', fontFamily: 'inherit' }}>{t('funnel.demoAtras')}</button>
          <button onClick={demoSiguiente} style={{ height: 34, padding: '0 16px', borderRadius: 999, border: 'none', background: 'linear-gradient(135deg,#3C8696,#2C6675)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>{step < TOTAL - 1 ? t('funnel.demoSiguiente') : t('funnel.demoTerminar')}</button>
        </div>
      )}
    </div>
  );
}
